// Changing a plan, before a session or during one (walkthrough 4): swap,
// add, remove, set counts, order. Pure: every change returns a new items
// array and leaves the one it was given alone. PlanEditor (and through it
// Today's plan and the session) is the only caller that renders them.
//
// An item the person brings in (a swap or an add) has no number from the
// engine until it answers (or if it can't), so it is "find your weight"
// (D9): no load, the exercise's own rep range, at least 3 in reserve (D8:
// its history isn't known here), and a reason saying so — every number
// keeps a reason (spec §5.7). Plan items carry only the keys the API's
// TrainingPlanItem knows, so a plan read back and saved again keeps its shape
// — but for `asking` (markAsking): before the start, an item brought in that
// is still waiting for the engine's numbers, which Start never sends (it
// waits for the answer).
import { MUSCLES, findGuidance, formatRir, itemReason } from "./format";

// backend/training/constants.py MAX_SETS_PER_ITEM, MAX_PLAN_ITEMS.
export const MIN_SETS = 1;
export const MAX_SETS = 10;
export const MAX_ITEMS = 30;
// engine_constants RIR_BASE: the reserve each role starts from.
export const ROLE_RIR = { main: 2, accessory: 1, shoulder_health: 3 };
// engine_constants NEW_RIR_MIN (D8): an exercise's first two sessions keep
// at least this in reserve — numbers made here don't know the history.
export const NEW_RIR = 3;
// engine_constants MAIN_SETS, ACCESSORY_SETS, SHOULDER_HEALTH_SETS (D10).
export const DEFAULT_SETS = { main: 3, accessory: 3, shoulder_health: 2 };
// D13: minutes for the warm-up and per set (engine fill._minutes).
export const WARMUP_MINUTES = { normal: 8, minimum: 5 };
export const SET_MINUTES = { main_heavy: 5, main: 4, accessory: 3, shoulder_health: 1.75 };
// The API's caps on a plan item's words (TrainingPlanItem / TrainingPlanWhy).
const REASON_MAX = 300;
const WHY_MAX = 500;

const ROLE_WORDS = {
  main: "a main lift",
  accessory: "an accessory",
  shoulder_health: "shoulder-health work",
};
const CATALOG_ORDER = new Map(MUSCLES.map((m, i) => [m.code, i]));

const clip = (text, max) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);
const clampSets = (n) => Math.min(MAX_SETS, Math.max(MIN_SETS, Math.round(Number(n) || MIN_SETS)));

/** The muscles an exercise trains as a primary (weight 1). */
export function primaryMuscles(exercise) {
  return Object.entries(exercise?.muscles ?? {})
    .filter(([, weight]) => Number(weight) >= 1)
    .map(([code]) => code)
    .sort((a, b) => (CATALOG_ORDER.get(a) ?? 99) - (CATALOG_ORDER.get(b) ?? 99));
}

/** The equipment the settings list — an adjustable bench lies flat, so it is a bench too (spec §5.8). */
export function gearOf(settings) {
  const have = new Set(settings?.equipment?.items ?? []);
  if (have.has("adjustable_bench")) have.add("bench");
  return have;
}

/** What an exercise needs that the settings don't list ([] = it fits). */
export function missingEquipment(exercise, settings) {
  const have = gearOf(settings);
  return (exercise?.requires ?? []).filter((item) => !have.has(item));
}

export const fitsEquipment = (exercise, settings) => missingEquipment(exercise, settings).length === 0;

/** Sets of `key` already logged (a `{key: count}` map; missing = 0). */
export function loggedCount(logged, key) {
  const n = Number(logged?.[key]);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** The sets of an item still to do. */
export function remainingSets(item, logged) {
  return Math.max(0, item.sets - loggedCount(logged, item.exercise));
}

const usable = (exercise) => Boolean(exercise) && !exercise.archived;

function split(list, settings) {
  const options = [];
  const unavailable = [];
  for (const exercise of list) {
    (fitsEquipment(exercise, settings) ? options : unavailable).push(exercise);
  }
  return { options, unavailable };
}

/**
 * What can replace `exercise`: its own alternatives first (the catalog's
 * order — "best first"), then other exercises for the same primary muscles,
 * those sharing more of them first, then the same role, then catalog order.
 * Leaves out the exercise itself, archived ones and the keys in `exclude`
 * (what's already in the plan). `options` fit the equipment; `unavailable`
 * are the same-muscle ones that need equipment the settings don't list (so
 * the picker can say what it left out).
 */
export function swapOptions(exercise, exercises, settings, { exclude = [] } = {}) {
  if (!exercise) return { options: [], unavailable: [] };
  const skip = new Set([exercise.key, ...exclude]);
  const byKey = new Map((exercises ?? []).map((e) => [e.key, e]));
  const mine = new Set(primaryMuscles(exercise));
  const alternatives = (exercise.alternatives ?? []).map((key) => byKey.get(key)).filter(Boolean);
  const first = new Set(alternatives.map((e) => e.key));
  const order = new Map((exercises ?? []).map((e, i) => [e.key, i]));
  const shared = (e) => primaryMuscles(e).filter((m) => mine.has(m)).length;
  const related = (exercises ?? [])
    .filter((e) => !first.has(e.key) && shared(e) > 0)
    .sort(
      (a, b) =>
        shared(b) - shared(a) ||
        Number(b.role === exercise.role) - Number(a.role === exercise.role) ||
        order.get(a.key) - order.get(b.key),
    );
  const all = [...alternatives, ...related].filter((e) => usable(e) && !skip.has(e.key));
  return split(all, settings);
}

/**
 * What can be added: every exercise not archived and not in `exclude`, in
 * catalog order, split by whether the equipment is there.
 */
export function addOptions(exercises, settings, { exclude = [] } = {}) {
  const skip = new Set(exclude);
  return split((exercises ?? []).filter((e) => usable(e) && !skip.has(e.key)), settings);
}

/** A find-your-weight item's reason: who brought it in, and whether the engine was asked in vain. */
function findReason(slotName, failed) {
  const lead = slotName ? `Swapped in for ${slotName}` : "Added by you";
  return clip(
    failed
      ? `${lead}. Couldn't get its numbers — your first set finds the weight.`
      : `${lead} — your first set finds the weight.`,
    REASON_MAX,
  );
}

/**
 * A plan item for an exercise the person brought in — "find your weight".
 * With `slot` (the item it replaces, on a swap): the slot's sets and, for
 * the same role, its reserve; a main lift for a main lift keeps the slot's
 * rep scheme and day type (chin-up ↔ lat pulldown). Otherwise the
 * exercise's own rep range and its role's usual reserve. Never under
 * NEW_RIR in reserve (D8). `failed`: the engine was asked for its numbers
 * and couldn't answer — the reason says so (W3).
 */
export function itemFor(exercise, { sets, slot, failed = false } = {}) {
  const role = ROLE_RIR[exercise.role] === undefined ? "accessory" : exercise.role;
  const name = exercise.name ?? exercise.key;
  const [low, high] = exercise.rep_range ?? [8, 12];
  const slotName = slot ? (slot.name ?? slot.exercise) : null;
  const keepScheme = Boolean(slot) && slot.role === "main" && role === "main";
  const keepRir = Boolean(slot) && slot.role === role && Number.isInteger(slot.rir);
  const count = clampSets(sets ?? DEFAULT_SETS[role]);
  const base = keepRir ? slot.rir : ROLE_RIR[role];
  const rir = Math.max(NEW_RIR, base);
  const target = keepScheme ? (slot.target_reps ?? slot.reps_low) : low;
  const item = {
    exercise: exercise.key,
    name,
    role,
    sets: count,
    reps_low: keepScheme ? slot.reps_low : low,
    reps_high: keepScheme ? slot.reps_high : high,
    target_reps: target,
    rir,
    load: null,
    find_weight: true,
    reason: findReason(slotName, failed),
    why: {
      sets: clip(
        slot
          ? `The ${count} ${count === 1 ? "set" : "sets"} ${slotName} had left.`
          : `${count} sets, the usual for ${ROLE_WORDS[role]} — change it if you like.`,
        WHY_MAX,
      ),
      reps: clip(
        keepScheme
          ? (slot.why?.reps ?? `${slotName}'s reps, kept for the swap.`)
          : `${name}'s rep range; aim for ${low}.`,
        WHY_MAX,
      ),
      rir: clip(
        rir !== base
          ? `${rir} in reserve — an exercise's first two sessions keep at least ${NEW_RIR}, and these numbers aren't from your history.`
          : keepRir
            ? (slot.why?.rir ?? `${slotName}'s reserve, kept for the swap.`)
            : `${rir} in reserve, the usual for ${ROLE_WORDS[role]}.`,
        WHY_MAX,
      ),
      // what to pick, in the engine's D9 words — the logging sheet says it too (f13)
      load: clip(findGuidance(exercise.load_kind, target, formatRir(rir)), WHY_MAX),
    },
  };
  if (keepScheme && slot.day_type) item.day_type = slot.day_type;
  return item;
}

/**
 * The engine's item for an exercise the person brought in (W3: POST
 * prescribe/ — history, seeds, D7–D9 as next/ would plan it), marked as
 * theirs: "Added by you." or "Swapped in for X." before the engine's reason
 * (without the name the card's title says). With `sets` (a swap during a
 * session) it takes the sets the slot had left; the engine's other numbers
 * stand.
 */
export function fromEngine(engineItem, exercise, { slot, sets } = {}) {
  const name = engineItem.name ?? exercise?.name ?? engineItem.exercise;
  const slotName = slot ? (slot.name ?? slot.exercise) : null;
  const lead = slot ? `Swapped in for ${slotName}.` : "Added by you.";
  const said = itemReason(engineItem.reason, name);
  const item = { ...engineItem, name, reason: clip(said ? `${lead} ${said}` : lead, REASON_MAX) };
  if (slot && sets !== undefined && sets !== null) {
    const count = clampSets(sets);
    item.sets = count;
    item.why = {
      ...(engineItem.why ?? {}),
      sets: clip(`The ${count} ${count === 1 ? "set" : "sets"} ${slotName} had left.`, WHY_MAX),
    };
  }
  return item;
}

/**
 * `item` (itemFor's stand-in, just brought in before the start) marked as
 * still asking the engine for its numbers. The mark goes into the draft with
 * the plan, so a reload asks again — the ask itself is lost with the page.
 * It keeps the key and name of what it was swapped in for (`slot`; null for
 * an add): the answer still says "Swapped in for X." after a reload.
 */
export function markAsking(item, slot = null) {
  return {
    ...item,
    asking: { slot: slot ? { exercise: slot.exercise, name: slot.name ?? slot.exercise } : null },
  };
}

/**
 * The engine couldn't answer for an item still asking: its find-your-weight
 * stand-in stays — the numbers as they are — its reason says the numbers
 * couldn't be got, and it stops asking. (itemFor's `failed` item, made
 * without the catalog or the slot it was swapped in for.)
 */
export function askFailed(item) {
  const { asking, ...rest } = item;
  const slot = asking?.slot;
  return { ...rest, reason: findReason(slot ? (slot.name ?? slot.exercise) : null, true) };
}

/** `items` with that very item (by identity, wherever it moved) replaced; the same array when it's gone. */
export function replaceItem(items, target, replacement) {
  const at = items.indexOf(target);
  if (at < 0) return items;
  return items.map((item, i) => (i === at ? replacement : item));
}

const CHANGED = /^You changed this to \d+\.(?: The plan's reason: )?/;

/** The item with a new set count, its reason saying who changed it. */
function withSets(item, sets, why) {
  const original = (item.why?.sets ?? "").replace(CHANGED, "").trim();
  const text =
    why ?? (original ? `You changed this to ${sets}. The plan's reason: ${original}` : `You changed this to ${sets}.`);
  return { ...item, sets, why: { ...(item.why ?? {}), sets: clip(text, WHY_MAX) } };
}

/**
 * Swap the item at `index` for `exercise`. Only the sets not yet logged
 * move (walkthrough 4): with some logged, the item keeps those under its
 * exercise and a new item right after it takes the rest. Nothing left to
 * do, an exercise already in the plan, or a bad index: unchanged. `make`
 * builds the new item from `(exercise, {sets: left, slot})` — itemFor's
 * numbers, or the engine's (fromEngine).
 */
export function swapAt(items, index, exercise, logged = {}, make = itemFor) {
  const item = items[index];
  if (!item || !exercise || items.some((other) => other.exercise === exercise.key)) return items;
  const done = Math.min(loggedCount(logged, item.exercise), item.sets);
  const left = item.sets - done;
  if (left <= 0) return items;
  const replacement = make(exercise, { sets: left, slot: item });
  const head = items.slice(0, index);
  const tail = items.slice(index + 1);
  if (done === 0) return [...head, replacement, ...tail];
  const kept = withSets(
    item,
    done,
    `${done} logged before the swap to ${replacement.name}; it took the other ${left}.`,
  );
  return [...head, kept, replacement, ...tail];
}

/**
 * Set the item's set count, held to MIN_SETS–MAX_SETS and never below the
 * sets already logged. Unchanged when the count doesn't move.
 */
export function setSetsAt(items, index, sets, logged = {}) {
  const item = items[index];
  if (!item) return items;
  const floor = Math.max(MIN_SETS, loggedCount(logged, item.exercise));
  const next = Math.min(MAX_SETS, Math.max(floor, Math.round(Number(sets) || 0)));
  if (next === item.sets) return items;
  return items.map((it, i) => (i === index ? withSets(it, next) : it));
}

/**
 * Remove the item at `index`. One with logged sets stays — its logged sets
 * are part of the session — and drops only the sets still to do.
 */
export function removeAt(items, index, logged = {}) {
  const item = items[index];
  if (!item) return items;
  const done = loggedCount(logged, item.exercise);
  if (done === 0) return items.filter((_, i) => i !== index);
  if (done >= item.sets) return items;
  return items.map((it, i) =>
    i === index ? withSets(it, done, `Stopped after the ${done} logged; the rest dropped.`) : it,
  );
}

/** Move the item at `index` one place up (−1) or down (+1). */
export function moveAt(items, index, direction) {
  const to = index + (direction < 0 ? -1 : 1);
  if (!items[index] || to < 0 || to >= items.length) return items;
  const out = items.slice();
  [out[index], out[to]] = [out[to], out[index]];
  return out;
}

/**
 * Add `exercise` at the end (as `item`: itemFor's numbers unless the
 * engine's are given) — unless it's already in the plan, or the plan is full.
 */
export function addItem(items, exercise, item = null) {
  if (!exercise || items.length >= MAX_ITEMS) return items;
  if (items.some((other) => other.exercise === exercise.key)) return items;
  return [...items, item ?? itemFor(exercise)];
}

/** Minutes the plan takes, the engine's way (D13): warm-up plus each set by role. */
export function estimateMinutes(items, template) {
  let total = template === "minimum" ? WARMUP_MINUTES.minimum : WARMUP_MINUTES.normal;
  for (const item of items ?? []) {
    let per = SET_MINUTES[item.role] ?? SET_MINUTES.accessory;
    if (item.role === "main") per = item.day_type === "heavy" ? SET_MINUTES.main_heavy : SET_MINUTES.main;
    total += (Number(item.sets) || 0) * per;
  }
  return Math.round(total * 10) / 10;
}
